import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

const GigiFormValidationContext = createContext(null)

export const GigiFormValidationProvider = ({ children }) => {
  const registry = useRef(new Map())
  const [errorMap, setErrorMap] = useState({})
  const [, force] = useState(0)
  const rerender = useCallback(() => force((n) => n + 1), [])

  const registerSection = useCallback(
    (sectionId, { isValid, focusFirstError, hasErrors }) => {
      registry.current.set(sectionId, { isValid, focusFirstError, hasErrors })
      rerender()
      return () => {
        registry.current.delete(sectionId)
        setErrorMap((prev) => {
          if (!(sectionId in prev)) return prev
          const next = { ...prev }
          delete next[sectionId]
          return next
        })
        rerender()
      }
    },
    [rerender]
  )

  /**
   * Sections report their error state to the context. Accepts either a number
   * (the count of invalid fields) or a boolean. Stored
   * normalized as a number so consumers can show "3 campi" instead of just
   * "1 sezione con errori".
   */
  const reportErrors = useCallback((sectionId, value) => {
    const count = typeof value === 'number' ? value : value ? 1 : 0
    setErrorMap((prev) => {
      if (prev[sectionId] === count) return prev
      return { ...prev, [sectionId]: count }
    })
  }, [])

  const validateAll = useCallback(async () => {
    const sectionsValid = await Promise.all(
      Array.from(registry.current.values()).map((s) => s.isValid())
    )
    return sectionsValid.every(Boolean)
  }, [])

  const focusFirstError = useCallback(() => {
    for (const section of registry.current.values()) {
      const focused = section.focusFirstError?.()
      if (focused) return true
    }
    return false
  }, [])

  const sectionHasErrors = useCallback((sectionId) => (errorMap[sectionId] || 0) > 0, [errorMap])
  const sectionErrorCount = useCallback((sectionId) => errorMap[sectionId] || 0, [errorMap])
  const totalErrorCount = useMemo(
    () => Object.values(errorMap).reduce((a, n) => a + (n || 0), 0),
    [errorMap]
  )

  const value = useMemo(
    () => ({
      registerSection,
      reportErrors,
      validateAll,
      focusFirstError,
      sectionHasErrors,
      sectionErrorCount,
      totalErrorCount,
      errorMap
    }),
    [
      registerSection,
      reportErrors,
      validateAll,
      focusFirstError,
      sectionHasErrors,
      sectionErrorCount,
      totalErrorCount,
      errorMap
    ]
  )

  return (
    <GigiFormValidationContext.Provider value={value}>
      {children}
    </GigiFormValidationContext.Provider>
  )
}

export const useGigiFormValidation = () => {
  const ctx = useContext(GigiFormValidationContext)
  if (!ctx) {
    return {
      registerSection: () => () => {},
      reportErrors: () => {},
      validateAll: async () => true,
      focusFirstError: () => false,
      sectionHasErrors: () => false,
      sectionErrorCount: () => 0,
      totalErrorCount: 0,
      errorMap: {}
    }
  }
  return ctx
}
